// ============================================================
// BcBillingCard.js — Business Central billing on a client's Billing tab
// (ruled 2026-10-21/22). A2Z admins only; the client never sees any of it.
//
// Order of work: billing email -> the Sales Lead (link it, or go on without
// one) -> Convert to paid -> (A2Z sends the mandate from BC) -> billing turns
// on by itself once the mandate is signed. Billing off, the MANUAL read-only
// after a conversation with the client, credit memos and the charges list
// live here too.
// ============================================================
import React, { useCallback, useEffect, useState } from 'react';
import {
  getBcBilling, searchLead, linkLead, skipLead, convertToPaid, linkExistingCustomer,
  billingOff, makeReadOnly, restoreAccess, creditCharge, retryCharge, runMonthEnd, STATE_LABELS,
} from '../api/bcBilling';
import { setClientBillingConfig, poundsFromPence } from '../api/billing';
import { toast } from './Toaster';
import StatusMessage from './StatusMessage';
import ConfirmDialog from './ConfirmDialog';

const err = (e, fallback) => (e && (e.detail || e.message)) || fallback;
const day = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { timeZone: 'Europe/London' }) : '');
const hhmm = (v) => (v ? new Date(v).toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' }) : '');

/** Why Convert to paid is offered again: the set-up after it did not finish. */
export function setupNotFinishedText(setup) {
  if (!setup || !setup.canRestart) return null;
  const e = setup.lastError;
  if (!e) return 'Set-up not finished: no BC customer yet. Convert to paid starts the Sales and BC set-up again.';
  const what = e.status ? `error ${e.status}` : 'error';
  return `Set-up not finished: last ${what} at ${hhmm(e.at)} on ${day(e.at)}${e.summary ? ` (${e.summary})` : ''}. Convert to paid starts the set-up again; nothing is created twice.`;
}
const KIND_LABELS = { monthly_arrears: 'Monthly (in arrears)', annual_advance: 'Annual (in advance)', credit: 'Credit memo' };

export default function BcBillingCard({ clientId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [search, setSearch] = useState(null);
  const [customerNo, setCustomerNo] = useState('');
  const [customerPreview, setCustomerPreview] = useState(null);
  const [roReason, setRoReason] = useState('');
  const [roInvoice, setRoInvoice] = useState('');
  const [roEmail, setRoEmail] = useState(true);
  const [period, setPeriod] = useState('');
  const [confirm, setConfirm] = useState(null); // 'convert' | 'off' | 'readonly' | 'restore' | { credit: charge }

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await getBcBilling(clientId);
      setData(d);
      setEmail((d.state && d.state.BillingContactEmail) || '');
      setContactName((d.state && d.state.BillingContactName) || '');
    } catch (e) { setError(err(e, 'Could not load Business Central billing.')); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  async function act(fn, ok) {
    setBusy(true);
    try { const r = await fn(); if (ok) toast.success(typeof ok === 'function' ? ok(r) : ok); await load(); return r; } catch (e) { toast.error(err(e, 'That did not work.')); return null; } finally { setBusy(false); setConfirm(null); }
  }

  if (error) return <div className="cbill__card"><StatusMessage variant="error">{error} <button className="button-secondary" onClick={load}>Retry</button></StatusMessage></div>;
  if (!data) return <div className="cbill__card"><StatusMessage variant="loading">Loading Business Central billing…</StatusMessage></div>;

  const s = data.state || {};
  const integ = data.integration || {};
  const state = s.BillingState || null;
  const canAct = integ.allowedForThisClient;
  const setup = data.setup || null;
  const restartText = setupNotFinishedText(setup);

  return (
    <div className="cbill__card bcb">
      <h3 className="cbill__title">Business Central billing</h3>
      <p className="cbill__hint">
        {integ.enabled ? `Switched on (${integ.mode}). ` : 'Switched off. '}
        {canAct ? 'Can act on this organisation.' : `Cannot act on this organisation (${integ.reason || 'not allowed'}). Convert to paid still marks the trial converted.`}
      </p>

      <dl className="bcb__facts">
        <dt>State</dt><dd>{state ? STATE_LABELS[state] || state : 'No billing settings yet'}</dd>
        <dt>Sales Lead</dt><dd>{s.LeadLinkDecision === 'linked' ? 'Linked' : s.LeadLinkDecision === 'none' ? 'None (going on without one)' : 'Not decided'}</dd>
        <dt>BC customer</dt><dd>{s.BcCustomerNumber || 'Not yet'}</dd>
        <dt>Mandate signed</dt><dd>{s.MandateSignedAt ? day(s.MandateSignedAt) : 'Not yet'}</dd>
        <dt>First month billed</dt><dd>{s.BillingStartPeriod || 'Not yet'}</dd>
        {s.BillingStoppedAt && (<><dt>Billing turned off</dt><dd>{day(s.BillingStoppedAt)}</dd></>)}
      </dl>

      <section className="bcb__section">
        <h4 className="bcb__h">Billing contact</h4>
        <p className="cbill__hint">BC sends invoices to this email address.</p>
        <div className="bcb__row">
          <label className="bcb__field">Name<input className="bcb__input" value={contactName} disabled={busy} onChange={(e) => setContactName(e.target.value)} /></label>
          <label className="bcb__field">Email<input className="bcb__input" type="email" value={email} disabled={busy} onChange={(e) => setEmail(e.target.value)} /></label>
          <button className="button-secondary" disabled={busy || !email}
            onClick={() => act(() => setClientBillingConfig(clientId, { billingContactEmail: email.trim(), billingContactName: contactName.trim() || null }), 'Billing contact saved.')}>Save</button>
        </div>
      </section>

      {(state === 'trial' || state == null) && (
        <section className="bcb__section">
          <h4 className="bcb__h">Sales Lead</h4>
          <p className="cbill__hint">Find the client's Lead in D365 Sales and confirm the right one. The Lead is then marked Trial and its outreach emails stop.</p>
          <div className="bcb__row">
            <button className="button-secondary" disabled={busy || !canAct} onClick={async () => {
              const r = await act(() => searchLead(clientId, email.trim() || undefined));
              if (r) setSearch(r);
            }}>Search with {email || 'the billing email'}</button>
            <button className="button-secondary" disabled={busy || !canAct} onClick={() => act(() => skipLead(clientId), 'Going on without a Lead.')}>Go on without a Lead</button>
          </div>
          {search && (
            <div className="bcb__results">
              {search.existingBcCustomers.length > 0 && (
                <p className="cbill__warn">Matches an existing BC customer: {search.existingBcCustomers.map((c) => `${c.number} ${c.name || ''}`).join(', ')}. Use "Link an existing BC customer" below instead.</p>
              )}
              {search.leads.length === 0 ? <p className="cbill__muted">No open Lead matched.</p> : (
                <table className="cbill__table">
                  <thead><tr><th>Company</th><th>Name</th><th>Email</th><th>Matched on</th><th /></tr></thead>
                  <tbody>{search.leads.map((l) => (
                    <tr key={l.leadId}>
                      <td>{l.company}</td><td>{[l.firstName, l.lastName].filter(Boolean).join(' ')}</td><td>{l.email}</td><td>{l.matchedOn}</td>
                      <td><button className="button-secondary" disabled={busy} onClick={() => act(() => linkLead(clientId, l.leadId), 'Lead linked; it is being marked Trial in Sales.')}>Use this Lead</button></td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          )}
        </section>
      )}

      <section className="bcb__section">
        <h4 className="bcb__h">Convert to paid</h4>
        <p className="cbill__hint">Marks the trial converted and the organisation active (an expired trial comes back). Then CareHub makes the Account and Contact in Sales, BC creates the customer, and you are alerted when it is ready for the Direct Debit mandate.</p>
        {restartText && <p className="bcb__notice" role="status">{restartText}</p>}
        {restartText && setup.lastError && setup.lastError.error && <p className="cbill__hint bcb__error-detail">{setup.lastError.error}</p>}
        <button className="button-primary" disabled={busy || state === 'billing_on' || (state === 'converted' && !(setup && setup.canRestart))} onClick={() => setConfirm('convert')}>Convert to paid</button>
      </section>

      <section className="bcb__section">
        <h4 className="bcb__h">Link an existing BC customer</h4>
        <p className="cbill__hint">For an organisation that is already an A2Z customer in BC. Set its payment method in BC first; billing starts once it has a signed mandate.</p>
        <div className="bcb__row">
          <label className="bcb__field">Customer number<input className="bcb__input" value={customerNo} disabled={busy} onChange={(e) => { setCustomerNo(e.target.value); setCustomerPreview(null); }} /></label>
          {!customerPreview ? (
            <button className="button-secondary" disabled={busy || !customerNo || !canAct} onClick={async () => {
              const r = await act(() => linkExistingCustomer(clientId, customerNo.trim(), false));
              if (r) setCustomerPreview(r.preview);
            }}>Look up</button>
          ) : (
            <>
              <span>{customerPreview.number}: {customerPreview.name} ({customerPreview.email || 'no email'})</span>
              <button className="button-primary" disabled={busy} onClick={() => act(() => linkExistingCustomer(clientId, customerPreview.number, true), 'BC customer linked.')}>Link this customer</button>
            </>
          )}
        </div>
      </section>

      {(state === 'billing_on' || state === 'converted') && (
        <section className="bcb__section">
          <h4 className="bcb__h">Billing off</h4>
          <p className="cbill__hint">The last full month is still invoiced on the 1st; the part-month is waived; annual modules are not refunded. Nothing changes in Sales or BC.</p>
          <button className="button-secondary" disabled={busy} onClick={() => setConfirm('off')}>Turn billing off</button>
        </section>
      )}

      <section className="bcb__section">
        <h4 className="bcb__h">Unpaid: make read-only</h4>
        <p className="cbill__hint">Only after talking to the client: people's care may depend on CareHub. Read-only modules are still charged.</p>
        {data.unpaidReadOnlyModules > 0 ? (
          <div className="bcb__row">
            <span className="cbill__warn">{data.unpaidReadOnlyModules} module(s) are read-only for an unpaid invoice.</span>
            <label className="bcb__check"><input type="checkbox" checked={roEmail} onChange={(e) => setRoEmail(e.target.checked)} /> Email the client</label>
            <button className="button-primary" disabled={busy} onClick={() => setConfirm('restore')}>Restore access</button>
          </div>
        ) : (
          <div className="bcb__row">
            <label className="bcb__field bcb__field--wide">Reason<input className="bcb__input" value={roReason} disabled={busy} onChange={(e) => setRoReason(e.target.value)} placeholder="The unpaid invoice and the conversation you had" /></label>
            <label className="bcb__field">BC invoice<input className="bcb__input" value={roInvoice} disabled={busy} onChange={(e) => setRoInvoice(e.target.value)} /></label>
            <label className="bcb__check"><input type="checkbox" checked={roEmail} onChange={(e) => setRoEmail(e.target.checked)} /> Email the client</label>
            <button className="button-secondary" disabled={busy || roReason.trim().length < 5} onClick={() => setConfirm('readonly')}>Make read-only</button>
          </div>
        )}
      </section>

      <section className="bcb__section">
        <h4 className="bcb__h">Charges</h4>
        {data.charges.length === 0 ? <p className="cbill__muted">No charges yet.</p> : (
          <div className="bcb__scroll">
            <table className="cbill__table">
              <thead><tr><th>CareHub ref</th><th>Kind</th><th>Month</th><th className="cbill__num">Total</th><th>Status</th><th>BC invoice</th><th>BC status</th><th>Direct Debit</th><th /></tr></thead>
              <tbody>{data.charges.map((c) => (
                <tr key={c.ChargeID} className={c.SupersededAt ? 'bcb__superseded' : undefined}>
                  <td>{c.ExternalRef}</td><td>{KIND_LABELS[c.Kind] || c.Kind}</td><td>{c.PeriodBilled}</td>
                  <td className="cbill__num">£{poundsFromPence(c.TotalPence)}</td>
                  <td>{c.Status}{c.LastError ? <div className="cbill__warn">{c.LastError}</div> : null}</td>
                  <td>{c.BcNumber || ''}</td><td>{c.BcStatus || ''}</td><td>{c.GcStatus || ''}</td>
                  <td className="cbill__actions">
                    {c.Status === 'failed' && <button className="button-secondary" disabled={busy} onClick={() => act(() => retryCharge(clientId, c.ChargeID), 'Will be sent again within the hour.')}>Retry</button>}
                    {c.Kind !== 'credit' && c.Status === 'posted' && c.BcStatus === 'Open' && <button className="button-secondary" disabled={busy} onClick={() => setConfirm({ credit: c })}>Credit</button>}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>

      {integ.mode === 'sandbox' && canAct && (
        <section className="bcb__section">
          <h4 className="bcb__h">Sandbox test: run a month end now</h4>
          <div className="bcb__row">
            <label className="bcb__field">Month (YYYY-MM)<input className="bcb__input" value={period} disabled={busy} onChange={(e) => setPeriod(e.target.value)} /></label>
            <button className="button-secondary" disabled={busy || !/^\d{4}-\d{2}$/.test(period)} onClick={() => act(() => runMonthEnd(clientId, period, false), (r) => `Saved ${r.saved.saved}, already there ${r.saved.duplicates}.`)}>Save charges</button>
            <button className="button-secondary" disabled={busy || !/^\d{4}-\d{2}$/.test(period)} onClick={() => act(() => runMonthEnd(clientId, period, true), (r) => `Saved ${r.saved.saved}; sent ${r.sent ? r.sent.charges : 0}.`)}>Save and send to BC</button>
          </div>
        </section>
      )}

      <ConfirmDialog isOpen={confirm === 'convert'} onClose={() => setConfirm(null)} isSubmitting={busy} title="Convert to paid" tone="primary"
        message="Mark this organisation as a paying client, and start the Sales and Business Central set-up?" confirmLabel="Convert to paid"
        onConfirm={() => act(() => convertToPaid(clientId), (r) => (r.bc === 'queued' ? 'Converted. You will be alerted when it is ready for the mandate.'
          : r.bc === 'restarted' ? 'Set-up started again. You will be alerted when it is ready for the mandate.'
          : r.bc === 'existing_bc_customer' ? 'Converted. It matches an existing BC customer: link it below.'
            : r.bc === 'not_run' ? 'Converted. Business Central was not involved for this organisation.' : 'Converted.'))} />
      <ConfirmDialog isOpen={confirm === 'off'} onClose={() => setConfirm(null)} isSubmitting={busy} title="Turn billing off"
        message="Stop billing this organisation? The last full month is still invoiced; nothing changes in Sales or BC." confirmLabel="Turn billing off"
        onConfirm={() => act(() => billingOff(clientId), 'Billing turned off.')} />
      <ConfirmDialog isOpen={confirm === 'readonly'} onClose={() => setConfirm(null)} isSubmitting={busy} title="Make read-only"
        message="Make every module read-only for this organisation? Staff will be able to view records but not add or change anything." confirmLabel="Make read-only"
        onConfirm={() => act(() => makeReadOnly(clientId, { reason: roReason.trim(), invoiceNumber: roInvoice.trim() || undefined, sendEmail: roEmail }), (r) => `${r.modulesMadeReadOnly} module(s) made read-only.`)} />
      <ConfirmDialog isOpen={confirm === 'restore'} onClose={() => setConfirm(null)} isSubmitting={busy} title="Restore access" tone="primary"
        message="Restore full access to the modules made read-only for an unpaid invoice?" confirmLabel="Restore access"
        onConfirm={() => act(() => restoreAccess(clientId, roEmail), (r) => `${r.modulesRestored} module(s) restored.`)} />
      <ConfirmDialog isOpen={!!(confirm && confirm.credit)} onClose={() => setConfirm(null)} isSubmitting={busy} title="Credit the whole invoice"
        message={confirm && confirm.credit ? `Create a credit memo for all of ${confirm.credit.BcNumber} (£${poundsFromPence(confirm.credit.TotalPence)})?` : ''} confirmLabel="Create credit memo"
        onConfirm={() => act(() => creditCharge(clientId, confirm.credit.ChargeID, { full: true }), (r) => `Credit ${r.creditRef}: ${r.outcome}${r.bcNumber ? ` (${r.bcNumber})` : ''}.`)} />
    </div>
  );
}
